import {ProductFilterConditionRestDto, ProductFilterRestDto} from "@/api/pharma-cpc-mgmt/models";
import {DpException} from "@/exception/DpException";
import {ProductFilterConditionRestDtoModel} from "@/models/api/pharma-cpc-mgmt/ProductFilterConditionRestDtoModel";
import {ProductFilterConditionTypeEnum} from "@/models/enum/ProductFilterConditionTypeEnum";
import {determineProductFilterConditionTypes} from "@/helpers/functions/catalog";

function addProductFilter(productFilter: ProductFilterRestDto, filterConditionType: ProductFilterConditionTypeEnum): void {
    if (productFilter.filter_conditions.length >= 4) {
        throw [new DpException('Maximum amount of filter conditions reached: ' + productFilter.filter_conditions.length)];
    }

    const conditionTypes: ProductFilterConditionTypeEnum[] = determineProductFilterConditionTypes(productFilter);

    if (!conditionTypes.length) {
        productFilter.filter_conditions.push(emptyFilterCondition(filterConditionType));
        prepareProductFilterForEditing(productFilter);
        return;
    }

    if (filterConditionType === ProductFilterConditionTypeEnum.ALL) {
        throw [new DpException('Condition type ALL can only be added if no other conditions exist')];
    }

    if (conditionTypes.includes(filterConditionType)) {
        throw [new DpException('The same condition type can only be added once')];
    }

    productFilter.filter_conditions.push(emptyFilterCondition(filterConditionType));
    prepareProductFilterForEditing(productFilter);
}

function removeEmptyProductFilterConditions(productFilter: ProductFilterRestDto): void {
    if (!productFilter.filter_conditions) {
        return;
    }
    productFilter.filter_conditions = productFilter.filter_conditions.filter((filterCondition: ProductFilterConditionRestDto) => {
        if (filterCondition.cnk_codes_included && !filterCondition.cnk_codes_included.length) {
            return false;
        }
        if (filterCondition.product_codes_included && !filterCondition.product_codes_included.length) {
            return false;
        }
        if (filterCondition.product_brand_filters && !filterCondition.product_brand_filters.length && !filterCondition.product_category_filters?.length) {
            return false;
        }

        return !(filterCondition.product_trademark_filters && !filterCondition.product_trademark_filters.length && !filterCondition.product_category_filters?.length);
    });
}

function prepareProductFilterForEditing(productFilter: ProductFilterRestDto): void {
    if (!productFilter.filter_conditions) {
        productFilter.filter_conditions = [];
    }
    const conditionTypes: ProductFilterConditionTypeEnum[] = determineProductFilterConditionTypes(productFilter);
    if (conditionTypes.includes(ProductFilterConditionTypeEnum.CNK_LIST) && !conditionTypes.includes(ProductFilterConditionTypeEnum.PRODUCT_CODE_LIST)) {
        addProductFilter(productFilter, ProductFilterConditionTypeEnum.PRODUCT_CODE_LIST);
    } else if (conditionTypes.includes(ProductFilterConditionTypeEnum.PRODUCT_CODE_LIST) && !conditionTypes.includes(ProductFilterConditionTypeEnum.CNK_LIST)) {
        addProductFilter(productFilter, ProductFilterConditionTypeEnum.CNK_LIST);
    }
}

function emptyFilterCondition(filter_condition_type: ProductFilterConditionTypeEnum): ProductFilterConditionRestDtoModel {
    switch (filter_condition_type) {
        case ProductFilterConditionTypeEnum.ALL:
            return {
                include_all_products: true,
                cnk_codes_excluded: [],
                product_codes_excluded: [],
            };
        case ProductFilterConditionTypeEnum.CNK_LIST:
            return {
                cnk_codes_included: [],
            };
        case ProductFilterConditionTypeEnum.PRODUCT_CODE_LIST:
            return {
                product_codes_included: [],
            };
        case ProductFilterConditionTypeEnum.BRANDS_CATEGORIES:
            return {
                cnk_codes_excluded: [],
                product_codes_excluded: [],
                product_brand_filters: [],
                product_category_filters: [],
            };
        case ProductFilterConditionTypeEnum.TRADEMARKS_CATEGORIES:
            return {
                cnk_codes_excluded: [],
                product_codes_excluded: [],
                product_trademark_filters: [],
                product_category_filters: [],
            };
        default:
            throw new DpException('Unsupported filter condition type: ' + filter_condition_type);
    }
}

export {
    addProductFilter,
    removeEmptyProductFilterConditions,
    prepareProductFilterForEditing,
    emptyFilterCondition
};
